import { useReducer, useCallback } from 'react';

import { httpBindAPI } from 'V2/Services/httpBindAPI';

type HttpMethod = 'GET' | 'POST';

interface Options<T> {
  method?: HttpMethod;
  headers?: Record<string, string>;
  transformResponse?: (data: unknown) => T;
}

export interface FetchArgs {
  params?: Record<string, any>;
  body?: any;
  appendPath?: string;
}

export interface FetchResult<T> {
  data: T | null;
  error: unknown;
  status: 'idle' | 'loading' | 'success' | 'error';
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  fetch: (args?: FetchArgs) => Promise<T>;
  update: (data: T) => void;
}

type State<T> =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: T | null; error: null }
  | { status: 'success'; data: T; error: null }
  | { status: 'error'; data: T | null; error: unknown };

type Action<T> =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: T }
  | { type: 'FETCH_ERROR'; error: unknown };

function reducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case 'FETCH_START':
      return { status: 'loading', data: state.data, error: null };
    case 'FETCH_SUCCESS':
      return { status: 'success', data: action.payload, error: null };
    case 'FETCH_ERROR':
      return { status: 'error', data: state.data, error: action.error };
    default:
      return state;
  }
}

export function useFetchMachine<T = unknown>(
  url: string,
  options?: Options<T>
): FetchResult<T> {
  const [state, dispatch] = useReducer(reducer<T>, {
    status: 'idle',
    data: null,
    error: null,
  });

  const update = useCallback((payload: T) => {
    dispatch({ type: 'FETCH_SUCCESS', payload });
  }, []);

  const fetch = useCallback(
    async ({ params, body, appendPath }: FetchArgs = {}): Promise<T> => {
      dispatch({ type: 'FETCH_START' });

      const method = options?.method ?? 'GET';
      const finalUrl = appendPath
        ? `${url.replace(/\/$/, '')}/${appendPath.replace(/^\//, '')}`
        : url;

      try {
        if (method === 'GET') {
          const response = await httpBindAPI
            .get<T>({
              url: finalUrl,
              data: params,
              headers: options?.headers,
            })
            .then((res) => {
              if (options?.transformResponse) {
                return options.transformResponse(res.data);
              }
              return res.data;
            });

          dispatch({ type: 'FETCH_SUCCESS', payload: response });

          return response;
        } else {
          const response = await httpBindAPI
            .post<T>({
              url: finalUrl,
              data: body,
              headers: options?.headers,
            })
            .then((res) => {
              if (options?.transformResponse) {
                return options.transformResponse(res.data);
              }
              return res.data;
            });

          dispatch({ type: 'FETCH_SUCCESS', payload: response });

          return response;
        }
      } catch (error) {
        dispatch({ type: 'FETCH_ERROR', error });
        throw error;
      }
    },
    [url] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    ...state,
    fetch,
    update,
    isIdle: state.status === 'idle',
    isLoading: state.status === 'loading',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
  };
}
